import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clientsApi from '../api/clientsApi';
import { toast } from 'react-toastify';

// Hook to fetch all clients
export const useClients = (params = {}) => {
  return useQuery({
    queryKey: ['clients', params],
    queryFn: () => clientsApi.getClients(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    keepPreviousData: true, // Keep previous data while fetching new data
  });
};

// Hook to fetch a single client
export const useClient = (clientId) => {
  return useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientsApi.getClient(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

// Hook to create a client
export const useCreateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clientData) => clientsApi.createClient(clientData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    },
  });
};

// Hook to update a client
export const useUpdateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, clientData }) => clientsApi.updateClient(clientId, clientData),
    onSuccess: (data, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      toast.success('Client updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    },
  });
};

// Hook to delete a client
export const useDeleteClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clientId) => clientsApi.deleteClient(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    },
  });
};

// Hook for bulk client updates
export const useBulkUpdateClients = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientIds, updates }) => clientsApi.bulkUpdateClients(clientIds, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Clients updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update clients');
    },
  });
};

// Hook to export clients
export const useExportClients = () => {
  return useMutation({
    mutationFn: (params) => clientsApi.exportClients(params),
    onSuccess: (blob, params) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clients-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Clients exported successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to export clients');
    },
  });
};

// Hook to fetch client labels
export const useClientLabels = () => {
  return useQuery({
    queryKey: ['client-labels'],
    queryFn: () => clientsApi.getClientLabels(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
  });
};